/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Building blocks for v2 settings pages (design reference "Settings · Phone
 * and softphone"): a section card with a title, a one-line summary and its
 * actions; list rows with an icon tile, two lines and right-hand detail; and
 * switch rows with a title, an explanation and the switch on the right.
 * Pages keep their own data and handlers and pass them in.
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { ChevronRight } from "lucide-react";
import { v2 as t, v2Tones } from "styles/tokens/v2";

export function SettingsSection({ title, summary, actions, children, flush }) {
  return (
    <Box
      component="section"
      aria-label={typeof title === "string" ? title : undefined}
      sx={{ bgcolor: t.surface, border: `1px solid ${t.border}`, borderRadius: "12px", mb: 2.5, overflow: "hidden" }}
    >
      {(title || actions) && (
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, px: 2.75, pt: 2.25, pb: children && !flush ? 1 : 2.25 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h2" sx={{ fontSize: 17, fontWeight: 600, color: t.text }}>
              {title}
            </Typography>
            {summary && <Typography sx={{ fontSize: 14, color: t.text2, mt: 0.25 }}>{summary}</Typography>}
          </Box>
          {actions && <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", "& .MuiButton-root": { whiteSpace: "nowrap" } }}>{actions}</Box>}
        </Box>
      )}
      {children && <Box sx={flush ? null : { px: 2.75, pb: 1.25 }}>{children}</Box>}
    </Box>
  );
}

SettingsSection.propTypes = {
  title: PropTypes.node,
  summary: PropTypes.node,
  actions: PropTypes.node,
  children: PropTypes.node,
  // Rows run edge to edge (a list) instead of inside the card's padding.
  flush: PropTypes.bool,
};
SettingsSection.defaultProps = { title: null, summary: null, actions: null, children: null, flush: false };

/** A status chip in one of the v2 tones. */
export function ToneChip({ tone, children }) {
  const c = v2Tones[tone] || v2Tones.neutral;
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, height: 24, px: 1.1, borderRadius: "12px", fontSize: 12.5, fontWeight: 600, bgcolor: c.bg, color: c.fg, whiteSpace: "nowrap" }}>
      {children}
    </Box>
  );
}

ToneChip.propTypes = { tone: PropTypes.oneOf(Object.keys(v2Tones)), children: PropTypes.node.isRequired };
ToneChip.defaultProps = { tone: "neutral" };

/**
 * One row of a list: icon tile, title and subtitle, detail text on the right,
 * then trailing content (a chip, a switch, an action). With `onClick` the
 * whole row opens something and shows a chevron.
 */
export function SettingsRow({ icon: Icon, title, subtitle, detail, trailing, onClick, disabled }) {
  const body = (
    <>
      {Icon && (
        <Box aria-hidden="true" sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: "10px", display: "grid", placeItems: "center", bgcolor: t.surface2, border: `1px solid ${t.border}`, color: t.text2 }}>
          <Icon size={18} />
        </Box>
      )}
      <Box sx={{ flex: "1 1 200px", minWidth: 0, textAlign: "left" }}>
        <Typography component="div" sx={{ fontSize: 15, fontWeight: 600, color: disabled ? t.textDisabled : t.text, fontVariantNumeric: "tabular-nums" }}>
          {title}
        </Typography>
        {subtitle && <Typography component="div" sx={{ fontSize: 13.5, color: t.text2, mt: 0.25 }}>{subtitle}</Typography>}
      </Box>
      {detail && <Typography component="div" sx={{ fontSize: 13.5, color: t.text2, textAlign: "right", ml: "auto" }}>{detail}</Typography>}
      {trailing && <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>{trailing}</Box>}
      {onClick && <ChevronRight size={18} color={t.text3} aria-hidden="true" />}
    </>
  );
  const rowSx = {
    display: "flex",
    alignItems: "center",
    flexWrap: { xs: "wrap", sm: "nowrap" },
    gap: 2,
    width: "100%",
    minHeight: 64,
    px: 2.75,
    py: 1.5,
    borderTop: `1px solid ${t.border}`,
    fontFamily: "inherit",
  };
  if (onClick) {
    return (
      <ButtonBase onClick={onClick} disabled={disabled} sx={{ ...rowSx, justifyContent: "flex-start", "&:hover": { bgcolor: t.hover }, "&:focus-visible": { outline: `2px solid ${t.link}`, outlineOffset: -2 } }}>
        {body}
      </ButtonBase>
    );
  }
  return <Box sx={rowSx}>{body}</Box>;
}

SettingsRow.propTypes = {
  icon: PropTypes.elementType,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  detail: PropTypes.node,
  trailing: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
};
SettingsRow.defaultProps = { icon: null, subtitle: null, detail: null, trailing: null, onClick: null, disabled: false };

/** A switch with its title and explanation, as in the mock-up's Softphone card. */
export function ToggleRow({ title, description, checked, onChange, disabled, first }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 3, py: 2, borderTop: first ? "none" : `1px solid ${t.border}` }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="div" sx={{ fontSize: 15, fontWeight: 600, color: t.text }}>
          {title}
        </Typography>
        {description && <Typography component="div" sx={{ fontSize: 14, color: t.text2, mt: 0.5 }}>{description}</Typography>}
      </Box>
      <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} inputProps={{ "aria-label": typeof title === "string" ? title : undefined }} />
    </Box>
  );
}

ToggleRow.propTypes = {
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  first: PropTypes.bool,
};
ToggleRow.defaultProps = { description: null, disabled: false, first: false };
